import React, { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Button,
  Table,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  FormGroup,
} from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import withAuth from "../withAuth"
import axios from "axios"
import { baseUrl } from "../../Network"
import { handleAuthError } from "../../utils"
import { toast } from "react-toastify"
import { LayoutDashboard } from "lucide-react"
import { useConfirm } from "../../contexts/ConfirmContext"
import { isMarketerReadOnly } from "../../helpers/adminRole"

const emptyCreate = {
  planKey: "",
  name: "",
  tagline: "",
  durationLabel: "",
  durationMonths: 1,
  periodSuffix: "",
  amountNgn: 0,
  amountUsd: 5,
  priceLabelNgn: "₦0",
  priceLabelUsd: "$5.00",
  stripePriceIdNgn: "",
  stripePriceIdUsd: "",
  paystackPlanCodeNgn: "",
  paystackPlanCodeUsd: "",
  isVisible: true,
  sortOrder: 10,
  featuresText: "Line one\nLine two",
}

const ManageAiSubscriptionPlans = () => {
  document.title = "AI subscription plans | RYD Admin"
  const { showConfirm } = useConfirm()
  const readOnly = isMarketerReadOnly()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [createMode, setCreateMode] = useState(true)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(emptyCreate)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const res = await axios.get(`${baseUrl}/admin/subscription/plans`)
      if (res?.data?.status) {
        setRows(Array.isArray(res.data.data) ? res.data.data : [])
      }
    } catch (e) {
      handleAuthError(e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const openCreate = () => {
    setCreateMode(true)
    setEditingId(null)
    setForm({ ...emptyCreate })
    setModal(true)
  }

  const openEdit = (r) => {
    setCreateMode(false)
    setEditingId(r.id)
    let featuresText = ""
    try {
      const f = JSON.parse(r.featuresJson || "[]")
      featuresText = Array.isArray(f) ? f.join("\n") : ""
    } catch {
      featuresText = ""
    }
    setForm({
      planKey: r.planKey,
      name: r.name,
      tagline: r.tagline || "",
      durationLabel: r.durationLabel || "",
      durationMonths: r.durationMonths || 1,
      periodSuffix: r.periodSuffix || "",
      amountNgn: r.amountNgn || 0,
      amountUsd: (r.amountUsdCents || 0) / 100,
      priceLabelNgn: r.priceLabelNgn || "",
      priceLabelUsd: r.priceLabelUsd || "",
      stripePriceIdNgn: r.stripePriceIdNgn || "",
      stripePriceIdUsd: r.stripePriceIdUsd || "",
      paystackPlanCodeNgn: r.paystackPlanCodeNgn || "",
      paystackPlanCodeUsd: r.paystackPlanCodeUsd || "",
      isVisible: !!r.isVisible,
      sortOrder: r.sortOrder ?? 10,
      featuresText,
    })
    setModal(true)
  }

  const submit = async () => {
    try {
      setSaving(true)
      const featuresJson = JSON.stringify(
        String(form.featuresText || "")
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean)
      )
      if (createMode) {
        const res = await axios.post(`${baseUrl}/admin/subscription/plan`, {
          planKey: String(form.planKey).trim(),
          name: String(form.name).trim(),
          tagline: form.tagline,
          durationLabel: form.durationLabel,
          durationMonths: Number(form.durationMonths) || 1,
          periodSuffix: form.periodSuffix,
          amountNgn: Number(form.amountNgn) || 0,
          amountUsdCents: Math.round(Number(form.amountUsd) * 100),
          priceLabelNgn: form.priceLabelNgn,
          priceLabelUsd: form.priceLabelUsd,
          stripePriceIdNgn: form.stripePriceIdNgn || undefined,
          stripePriceIdUsd: form.stripePriceIdUsd || undefined,
          paystackPlanCodeNgn: form.paystackPlanCodeNgn || undefined,
          paystackPlanCodeUsd: form.paystackPlanCodeUsd || undefined,
          featuresJson,
          isVisible: form.isVisible,
          sortOrder: Number(form.sortOrder) || 0,
        })
        if (res?.data?.status) {
          toast.success(res?.data?.message || "Plan created")
        } else {
          toast.error(res?.data?.message || "Could not create plan")
          return
        }
      } else {
        const res = await axios.put(`${baseUrl}/admin/subscription/plan/${editingId}`, {
          name: String(form.name).trim(),
          tagline: form.tagline,
          durationLabel: form.durationLabel,
          durationMonths: Number(form.durationMonths) || 1,
          periodSuffix: form.periodSuffix,
          amountNgn: Number(form.amountNgn) || 0,
          amountUsdCents: Math.round(Number(form.amountUsd) * 100),
          priceLabelNgn: form.priceLabelNgn,
          priceLabelUsd: form.priceLabelUsd,
          stripePriceIdNgn: form.stripePriceIdNgn || null,
          stripePriceIdUsd: form.stripePriceIdUsd || null,
          paystackPlanCodeNgn: form.paystackPlanCodeNgn || null,
          paystackPlanCodeUsd: form.paystackPlanCodeUsd || null,
          featuresJson,
          isVisible: form.isVisible,
          sortOrder: Number(form.sortOrder) || 0,
        })
        if (res?.data?.status) {
          toast.success(res?.data?.message || "Plan updated")
        } else {
          toast.error(res?.data?.message || "Could not update plan")
          return
        }
      }
      setModal(false)
      await load()
    } catch (e) {
      handleAuthError(e)
      toast.error(e?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  const toggleVis = async (r) => {
    try {
      const res = await axios.put(`${baseUrl}/admin/subscription/plan/${r.id}`, { isVisible: !r.isVisible })
      if (res?.data?.status) {
        toast.success(res?.data?.message || `Plan ${r.isVisible ? "hidden" : "shown"}`)
      } else {
        toast.error(res?.data?.message || "Visibility update failed")
        return
      }
      await load()
    } catch (e) {
      handleAuthError(e)
      toast.error(e?.response?.data?.message || "Visibility update failed")
    }
  }

  const remove = async (r) => {
    if (r.isSystem) {
      toast.error("System plans cannot be deleted")
      return
    }
    const ok = await showConfirm({
      title: "Delete plan",
      message: `Remove plan “${r.name}”?`,
      variant: "danger",
    })
    if (!ok) return
    try {
      await axios.delete(`${baseUrl}/admin/subscription/plan/${r.id}`)
      toast.success("Deleted")
      await load()
    } catch (e) {
      handleAuthError(e)
    }
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row className="align-items-center mb-3">
            <Col md={8}>
              <Breadcrumbs title="AI" breadcrumbItem="Subscription plans" />
            </Col>
            <Col md={4} className="text-md-end d-flex gap-2 justify-content-md-end flex-wrap">
              <Button tag={Link} to="/ai/dashboard" color="light" size="sm">
                <LayoutDashboard size={16} className="me-1" style={{ verticalAlign: "text-top" }} />
                AI home
              </Button>
              <Button tag={Link} to={readOnly ? "/ai/dashboard" : "/dashboard"} color="light" size="sm">
                Main dashboard
              </Button>
              {!readOnly && (
              <Button color="primary" size="sm" onClick={openCreate}>
                Add plan
              </Button>
              )}
            </Col>
          </Row>
          <Card>
            <CardBody>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="align-middle table-hover">
                    <thead>
                      <tr>
                        <th>Key</th>
                        <th>Name</th>
                        <th>Price</th>
                        <th>Duration</th>
                        <th>Visible</th>
                        <th>System</th>
                        {!readOnly && <th />}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <code>{r.planKey}</code>
                          </td>
                          <td>{r.name}</td>
                          <td className="small">
                            {r.priceLabelNgn} / {r.priceLabelUsd}
                          </td>
                          <td className="small text-muted">
                            {r.durationLabel || `${r.durationMonths || 1} month(s)`}
                          </td>
                          <td>{r.isVisible ? "Yes" : "No"}</td>
                          <td>{r.isSystem ? "Yes" : "—"}</td>
                          {!readOnly && (
                          <td className="text-end">
                            <Button size="sm" color="secondary" outline className="me-1" onClick={() => openEdit(r)}>
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              color="info"
                              outline
                              className="me-1"
                              onClick={() => toggleVis(r)}
                            >
                              {r.isVisible ? "Hide" : "Show"}
                            </Button>
                            {!r.isSystem && (
                              <Button size="sm" color="danger" outline onClick={() => remove(r)}>
                                Delete
                              </Button>
                            )}
                          </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      {!readOnly && (
      <Modal isOpen={modal} toggle={() => setModal(false)} size="lg">
        <ModalHeader toggle={() => setModal(false)}>{createMode ? "Add plan" : "Edit plan"}</ModalHeader>
        <ModalBody>
          {createMode && (
            <FormGroup>
              <Label>Plan key (unique, slug)</Label>
              <Input
                value={form.planKey}
                onChange={(e) => setForm((f) => ({ ...f, planKey: e.target.value }))}
                placeholder="e.g. quarterly"
              />
            </FormGroup>
          )}
          <FormGroup>
            <Label>Display name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </FormGroup>
          <FormGroup>
            <Label>Tagline</Label>
            <Input
              value={form.tagline}
              onChange={(e) => setForm((f) => ({ ...f, tagline: e.target.value }))}
            />
          </FormGroup>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Duration label</Label>
                <Input
                  value={form.durationLabel}
                  onChange={(e) => setForm((f) => ({ ...f, durationLabel: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Duration (months)</Label>
                <Input
                  type="number"
                  value={form.durationMonths}
                  onChange={(e) => setForm((f) => ({ ...f, durationMonths: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Sort order</Label>
                <Input
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label>Period suffix</Label>
            <Input
              value={form.periodSuffix}
              onChange={(e) => setForm((f) => ({ ...f, periodSuffix: e.target.value }))}
              placeholder="/ month"
            />
          </FormGroup>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Amount (NGN, whole naira)</Label>
                <Input
                  type="number"
                  value={form.amountNgn}
                  onChange={(e) => setForm((f) => ({ ...f, amountNgn: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Price label NGN</Label>
                <Input
                  value={form.priceLabelNgn}
                  onChange={(e) => setForm((f) => ({ ...f, priceLabelNgn: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Stripe Price ID (NGN)</Label>
                <Input
                  value={form.stripePriceIdNgn}
                  onChange={(e) => setForm((f) => ({ ...f, stripePriceIdNgn: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Paystack Plan Code (NGN)</Label>
                <Input
                  value={form.paystackPlanCodeNgn}
                  onChange={(e) => setForm((f) => ({ ...f, paystackPlanCodeNgn: e.target.value }))}
                  placeholder="Created automatically on first Paystack checkout"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Amount (USD)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.amountUsd}
                  onChange={(e) => setForm((f) => ({ ...f, amountUsd: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Price label USD</Label>
                <Input
                  value={form.priceLabelUsd}
                  onChange={(e) => setForm((f) => ({ ...f, priceLabelUsd: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Stripe Price ID (USD)</Label>
                <Input
                  value={form.stripePriceIdUsd}
                  onChange={(e) => setForm((f) => ({ ...f, stripePriceIdUsd: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <Label>Paystack Plan Code (USD)</Label>
                <Input
                  value={form.paystackPlanCodeUsd}
                  onChange={(e) => setForm((f) => ({ ...f, paystackPlanCodeUsd: e.target.value }))}
                  placeholder="Created automatically on first Paystack checkout"
                />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label>Features (one per line)</Label>
            <Input
              type="textarea"
              rows={4}
              value={form.featuresText}
              onChange={(e) => setForm((f) => ({ ...f, featuresText: e.target.value }))}
            />
          </FormGroup>
          <FormGroup check>
            <Input
              type="checkbox"
              id="vis"
              checked={form.isVisible}
              onChange={(e) => setForm((f) => ({ ...f, isVisible: e.target.checked }))}
            />
            <Label check for="vis">
              Visible on public/parent plan lists
            </Label>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setModal(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={submit} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </ModalFooter>
      </Modal>
      )}
    </React.Fragment>
  )
}

export default withAuth(ManageAiSubscriptionPlans)
